import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AppRemixIcon,
  AppCard,
  AppInput,
  AppDeleteModal,
  AppNoInfo,
  AppMultiSelect,
  AppCheckbox,
  AppModal,
} from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import NoInfo from '../../../layout/components/helpers/NoInfo';
import colors from '../../../constants/colors';
import SpinnerLoader from '../../../layout/components/helpers/SpinnerLoader';
import UserManagementModuleSidebar from '../../../layout/screenSpecifics/Modules/UserManagement/UserManagementModuleSidebar';
import UserManagementEditModuleCard from '../../../layout/screenSpecifics/Modules/UserManagement/UserManagementEditModuleCard ';
import Translator from '../../../layout/components/helpers/Translator';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import AppSpinner from '../../../layout/components/AppSpinner';
import errorHandler from '@/layout/components/error/ErrorHandler';

export default function UserManagementEditScreen() {
  const { moduleBridgeHex, userName } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const isSidebarOpened = useSelector(
    (state) => state.auth?.settings?.preferences?.sidebar?.isOpened
  );
  const [moduleBridge, setModuleBridge] = useState();
  const [modules, setModules] = useState();
  const [user, setUser] = useState();
  const [referenceModulesSelector, setReferenceModulesSelector] = useState();
  const [referenceUser, setReferenceUser] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [emailDomains, setEmailDomains] = useState();
  const [validationSchema] = useState(
    Yup.object().shape({
      name: Yup.string().required(
        ReactDOMServer.renderToString(
          <Translator
            file="module-userManagement"
            name="module.um.enter-name"
          />
        )
      ),
      email: Yup.string()
        .required(
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.enter-valid-email-address"
            />
          )
        )
        .email(
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.enter-valid-email-address"
            />
          )
        )
        .test(
          'check-domain',
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.domain-not-supported"
            />
          ),
          function (v) {
            if (this.parent.emailDomains === false) return true;

            if (this.parent.emailDomains === 'unauthorized' || !v) return false;

            const tempEmail = this.parent.email?.split('@');
            return this.parent.emailDomains.includes(tempEmail[1]);
          }
        ),
      confirmEmail: Yup.string()
        .required(
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.enter-valid-email-address"
            />
          )
        )
        .email(
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.enter-valid-email-address"
            />
          )
        )
        .test(
          'check-mail2',
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.email-does-not-match"
            />
          ),
          function (v) {
            if (!v) return false;
            return this.parent.email === v;
          }
        )
        .test(
          'check-domain',
          ReactDOMServer.renderToString(
            <Translator
              file="module-userManagement"
              name="module.um.domain-not-supported"
            />
          ),
          function (v) {
            if (this.parent.emailDomains === false) return true;

            if (this.parent.emailDomains === 'unauthorized' || !v) return false;

            const tempEmail = this.parent.email?.split('@');
            return this.parent.emailDomains.includes(tempEmail[1]);
          }
        ),
    })
  );

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  const loadInfoUser = useCallback(async () => {
    await axios
      .post(axiosPath('api_r_user_module_userManagement_user_edit_get'), {
        userName,
        moduleBridgeHex,
      })
      .then(({ data }) => {
        if (isMounted.current) {
          setModuleBridge(data.moduleBridge);
          setUser(data.user);
          setModules(data.modules);
          setReferenceModulesSelector(data.referenceModulesSelector);
          setReferenceUser(data.referenceUser);
          setEmailDomains(data.emailDomains);
        }
      })
      .catch((e) => dispatch(ErrorHandler(e, history)));
  }, [userName]);

  useEffect(() => {
    loadInfoUser();
  }, []);

  const handleDeleteUser = useCallback(async () => {
    await axios
      .delete(axiosPath('api_r_user_module_userManagement_user_delete'), {
        data: {
          userID: user.id,
          moduleBridgeHex,
        },
      })
      .then(() => {
        if (isMounted.current) {
          navigate(
            path({
              name: 'user_module_userManagement_home',
              params: {
                moduleBridgeHex,
              },
            })
          );
        }
      })
      .catch((e) => dispatch(errorHandler(e, history)))
      .finally(() => setShowDeleteModal(false));
  }, [user]);

  const handleEditUser = useCallback(
    async (payload) => {
      await axios
        .post(axiosPath('api_r_user_module_userManagement_user_edit'), {
          userName,
          moduleBridgeHex,
          payload,
        })
        .then((r) => {
          if (isMounted.current) {
            navigate(
              path({
                name: 'user_module_userManagement_home',
                params: {
                  moduleBridgeHex,
                },
              })
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e, history)));
    },
    [userName, moduleBridgeHex]
  );

  return (
    <div>
      <BreadcrumbHandler
        paths={
          moduleBridge
            ? [
                {
                  name: moduleBridge?.name,
                  route: {
                    name: 'user_module_userManagement_home',
                    params: { moduleBridgeHex },
                  },
                },
                {
                  name: `${ReactDOMServer.renderToString(
                    <Translator
                      file="module-userManagement"
                      name="module.um.user-information"
                    />
                  )} ${userName}`,
                },
              ]
            : [{ name: '...' }]
        }
      />

      <UserManagementModuleSidebar
        permissionLevel={referenceUser?.permissionLevel}
      />

      {user && modules ? (
        <Formik
          initialValues={{
            name: user.name,
            user: user.user,
            email: user.email,
            confirmEmail: user.email,
            addPlants: [],
            deletePlants: [],
            addModules: [],
            addModulesEmailNotifications: true,
            newsletterNotification: user.hasNewsletter,
            modules,
            emailDomains,
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
          onSubmit={(v) => handleEditUser(v)}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <main role="main" className="col-md-12 col-lg-12 px-md-4">
                  <div className={`${isSidebarOpened && 'sidebar-expanded'}`}>
                    <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-0 mb-0 border-bottom">
                      <AppCard
                        header={
                          <div>
                            <span>
                              {ReactDOMServer.renderToString(
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.user-information"
                                />
                              )}
                            </span>
                            <span
                              className="float-right text-danger pointer  border border-danger"
                              onClick={() => setShowDeleteModal(true)}
                            >
                              <span className="pl-1 pr-1 d-inline-flex">
                                <AppRemixIcon icon="delete-bin" />{' '}
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.delete"
                                />
                              </span>
                            </span>
                          </div>
                        }
                      >
                        {user && modules ? (
                          <>
                            <AppInput
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.user"
                                />
                              )}
                              icon={<AppRemixIcon icon="user" />}
                              value={values.user}
                              isReadOnly
                              isDisabled
                            />
                            <AppInput
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.name"
                                />
                              )}
                              icon={<AppRemixIcon icon="user" />}
                              value={values.name}
                              onChange={(value) => setFieldValue('name', value)}
                            />
                            <AppInput
                              icon={<AppRemixIcon icon="mail" />}
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.email"
                                />
                              )}
                              value={values.email}
                              onChange={(value) =>
                                setFieldValue('email', value)
                              }
                              error={errors.email}
                            />
                            <AppInput
                              icon={<AppRemixIcon icon="mail" />}
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.confirm-email"
                                />
                              )}
                              value={values.confirmEmail}
                              onChange={(value) =>
                                setFieldValue('confirmEmail', value)
                              }
                              error={errors.confirmEmail}
                            />
                            <AppCheckbox
                              value={values.newsletterNotification}
                              onChange={(v) =>
                                setFieldValue('newsletterNotification', v)
                              }
                              hasBorder
                            >
                              <Translator
                                file="module-userManagement"
                                name="module.um.receive-newsletters-email"
                              />
                            </AppCheckbox>
                          </>
                        ) : (
                          <NoInfo
                            icon={<SpinnerLoader />}
                            text={ReactDOMServer.renderToString(
                              <Translator
                                file="module-userManagement"
                                name="module.um.loading"
                              />
                            )}
                            color={colors.primary}
                          />
                        )}
                      </AppCard>

                      {referenceModulesSelector && (
                        <AppCard
                          header={ReactDOMServer.renderToString(
                            <Translator
                              file="module-userManagement"
                              name="module.um.add-new-modules"
                            />
                          )}
                          className="mt-3"
                        >
                          <AppMultiSelect
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-userManagement"
                                name="module.um.modules"
                              />
                            )}
                            icon={<AppRemixIcon icon="list-check" noVariants />}
                            value={values.addModules}
                            options={referenceModulesSelector}
                            onChange={(v) => setFieldValue('addModules', v)}
                            hasSearchBox
                          />

                          <AppCheckbox
                            value={values.addModulesEmailNotifications}
                            onChange={(v) =>
                              setFieldValue('addModulesEmailNotifications', v)
                            }
                          >
                            <Translator
                              file="module-userManagement"
                              name="module.um.notification-obligations-email-only-created"
                            />
                          </AppCheckbox>
                        </AppCard>
                      )}

                      <AppCard
                        header={
                          <div>
                            <div className="d-inline-flex">
                              <span className="mt-1">
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.user-modules"
                                />
                              </span>
                            </div>
                          </div>
                        }
                        className="mt-3"
                      >
                        {user && modules ? (
                          <>
                            {values.modules.map((module) => (
                              <UserManagementEditModuleCard
                                module={module}
                                key={module.id}
                                setFieldValue={setFieldValue}
                                values={values}
                              />
                            ))}
                          </>
                        ) : (
                          <NoInfo
                            icon={<SpinnerLoader />}
                            text={ReactDOMServer.renderToString(
                              <Translator
                                file="module-userManagement"
                                name="module.um.loading"
                              />
                            )}
                            color={colors.primary}
                          />
                        )}

                        <div className="mt-3 float-right">
                          <button
                            type="submit"
                            className="btn btn-xs btn-outline-light"
                            disabled={isSubmitting}
                          >
                            <Translator
                              file="module-userManagement"
                              name="module.um.update-user"
                            />
                          </button>
                        </div>
                      </AppCard>
                    </div>
                  </div>
                </main>
              </div>

              <AppModal show={isSubmitting} title="Actualizando usuario">
                <div className="text-center">
                  <div>
                    <AppSpinner>
                      <AppRemixIcon
                        icon="loader-4"
                        size={40}
                        color={colors.primary}
                      />
                    </AppSpinner>
                  </div>
                  <div>Realizando la petición. Por favor, espere</div>
                </div>
              </AppModal>
            </form>
          )}
        </Formik>
      ) : (
        <AppNoInfo
          icon={<SpinnerLoader />}
          text={ReactDOMServer.renderToString(
            <Translator file="module-userManagement" name="module.um.loading" />
          )}
        />
      )}
      <AppDeleteModal
        show={showDeleteModal}
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-userManagement"
            name="module.um.delete-user"
          />
        )}
        size="lg"
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => handleDeleteUser()}
        sensitive
        sensitiveWord={ReactDOMServer.renderToString(
          <Translator
            file="module-userManagement"
            name="module.um.delete-user"
          />
        )}
      />
    </div>
  );
}
